import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Spinner } from 'flowbite-react';
import {
  HiArrowLeft,
  HiOutlineDownload,
  HiOutlineExternalLink,
} from 'react-icons/hi';
import { toast, ToastContainer } from 'react-toastify';
import Button from '../../../components/Button';
import DefaultModal from '../../../components/modals/DefaultModal';
import { useActorEmail } from '../../../hooks/useActorEmail';
import {
  advanceRmJob,
  cancelRmJob,
  correctRmJob,
  fetchRmFileDownload,
  fetchRmJob,
  markRmJobReady,
  uploadRmMagazine,
} from './api';
import {
  actorChipClass,
  actorLabel,
  ageText,
  CANCEL_REASON_MAX_LENGTH,
  CANCEL_REASON_MIN_LENGTH,
  CORRECTION_REASON_MAX_LENGTH,
  correctionStagesOf,
  customizationDisplay,
  errorMessage,
  fileUnavailableWording,
  formatDateTime,
  formatFileSize,
  formatLabel,
  heroAction,
  historyChronological,
  historyDetail,
  historySummary,
  issueLabel,
  magazineClientError,
  refusalCode,
  sourceChipClass,
  sourceLabel,
  stageLabel,
  stepStates,
  validateCancelReason,
} from './helpers';
import { formatMessage, rmQueueStrings } from './strings';
import {
  RmAdvanceStage,
  RmCorrectionStage,
  RmFileKind,
  RmFileStatus,
  RmJobDetail,
} from './types';

const RM_JOB_QUERY = 'rm-print-job';
const { detail } = rmQueueStrings;

/** Refusals that mean the page is showing a job that has since changed. */
const STALE_JOB_CODES = new Set([
  'RM_PRINT_JOB_STAGE_CONFLICT',
  'RM_PRINT_JOB_NOT_FOUND',
  'RM_PRINT_JOB_NOT_CANCELLABLE',
  'RM_PRINT_JOB_FILE_LOCKED',
  'RM_PRINT_JOB_NO_PRINT_FILE',
]);

/** A 36px secondary action, the size the print-shop pages use for file actions. */
const SMALL_BUTTON =
  'inline-flex h-9 items-center gap-1.5 rounded-lg border border-primary-300 bg-white px-3 text-sm font-semibold text-primary-700 transition hover:bg-primary-50 disabled:opacity-60';
const LINK_BUTTON =
  'text-sm font-semibold text-primary-600 hover:underline disabled:opacity-60 disabled:no-underline';
const HERO_LINK =
  'text-xs font-semibold text-white/80 underline transition hover:text-white';
const CARD = 'rounded-xl border border-neutral-200 bg-white p-5 shadow-sm';
const CARD_TITLE = 'text-xs font-bold uppercase tracking-wider text-neutral-400';

export default function RmJobDetailPage() {
  const { jobId = '' } = useParams<{ jobId: string }>();
  const queryClient = useQueryClient();
  const actorEmail = useActorEmail();

  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const { data: job, isLoading, isError } = useQuery(
    [RM_JOB_QUERY, jobId],
    () => fetchRmJob(jobId),
    { refetchOnWindowFocus: false, retry: false },
  );

  const invalidate = () => {
    queryClient.invalidateQueries([RM_JOB_QUERY, jobId]);
    queryClient.invalidateQueries('rm-print-jobs');
    queryClient.invalidateQueries('rm-print-jobs-counts');
  };

  /** A refused action: the text of its code, and a reload when the job moved. */
  const onActionError = (error: unknown) => {
    toast.error(errorMessage(error, detail.toast.actionFailed));
    const code = refusalCode(error);
    if (code && STALE_JOB_CODES.has(code)) {
      invalidate();
    }
  };

  const advanceMutation = useMutation<RmJobDetail, unknown, RmAdvanceStage>(
    (to: RmAdvanceStage) => advanceRmJob(jobId, to, actorEmail),
    {
      onSuccess: (_data, to) => {
        invalidate();
        setAdvanceOpen(false);
        toast.success(
          formatMessage(detail.toast.advanced, { to: stageLabel(to) }),
        );
      },
      onError: (error) => {
        setAdvanceOpen(false);
        onActionError(error);
      },
    },
  );

  const readyMutation = useMutation<RmJobDetail, unknown, void>(
    () => markRmJobReady(jobId, actorEmail),
    {
      onSuccess: () => {
        invalidate();
        toast.success(detail.toast.ready);
      },
      onError: onActionError,
    },
  );

  const correctMutation = useMutation<
    RmJobDetail,
    unknown,
    { to: RmCorrectionStage; reason: string }
  >(({ to, reason }) => correctRmJob(jobId, to, reason, actorEmail), {
    onSuccess: (_data, { to }) => {
      invalidate();
      setCorrectOpen(false);
      toast.success(
        formatMessage(detail.toast.corrected, { to: stageLabel(to) }),
      );
    },
    onError: (error) => {
      setCorrectOpen(false);
      onActionError(error);
    },
  });

  const cancelMutation = useMutation<RmJobDetail, unknown, string>(
    (reason: string) => cancelRmJob(jobId, reason, actorEmail),
    {
      onSuccess: () => {
        invalidate();
        setCancelOpen(false);
        toast.success(detail.toast.cancelled);
      },
      onError: (error) => {
        setCancelOpen(false);
        onActionError(error);
      },
    },
  );

  if (isLoading) {
    return (
      <div className="flex justify-center pt-16">
        <Spinner data-testid="rm-job-loading" />
      </div>
    );
  }

  if (isError || !job) {
    return (
      <div
        data-testid="rm-job-error"
        className="pt-16 text-center text-neutral-500"
      >
        {rmQueueStrings.queue.error}
      </div>
    );
  }

  const correctionStages = correctionStagesOf(job);
  const action = heroAction(job);

  return (
    <div className="pt-8 min-h-screen w-full" data-testid="rm-job-detail">
      <ToastContainer position="top-right" autoClose={4000} />

      <Link
        to="/print-shop/rm"
        className="mb-4 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-700"
      >
        <HiArrowLeft className="h-4 w-4" /> {detail.backToQueue}
      </Link>

      <RmHero
        job={job}
        action={action}
        busy={readyMutation.isLoading || advanceMutation.isLoading}
        onReady={() => readyMutation.mutate()}
        onAdvance={() => setAdvanceOpen(true)}
        onCorrect={correctionStages.length > 0 ? () => setCorrectOpen(true) : undefined}
        onCancel={job.actions?.cancel ? () => setCancelOpen(true) : undefined}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <RmPrintFiles
            job={job}
            actorEmail={actorEmail}
            onUploaded={invalidate}
            onStale={invalidate}
          />
          <RmAbout job={job} />
        </div>
        <RmHistory job={job} />
      </div>

      {action.kind === 'advance' && (
        <DefaultModal
          title={detail.advance.title}
          showModal={advanceOpen}
          setShowModal={(show) => {
            if (!show && !advanceMutation.isLoading) setAdvanceOpen(false);
          }}
          size="md"
        >
          {advanceOpen && (
            <div className="space-y-4" data-testid="rm-advance-dialog">
              <p className="text-sm text-neutral-600">
                {formatMessage(detail.advance.body, {
                  from: stageLabel(job.stage),
                  to: stageLabel(action.to),
                })}
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="OUTLINED"
                  disabled={advanceMutation.isLoading}
                  onClick={() => setAdvanceOpen(false)}
                >
                  {detail.advance.keep}
                </Button>
                <Button
                  variant="PRIMARY"
                  data-testid="rm-advance-confirm"
                  isProcessing={advanceMutation.isLoading}
                  disabled={advanceMutation.isLoading}
                  onClick={() => advanceMutation.mutate(action.to)}
                >
                  {formatMessage(detail.advance.confirm, {
                    to: stageLabel(action.to).toLowerCase(),
                  })}
                </Button>
              </div>
            </div>
          )}
        </DefaultModal>
      )}

      <RmCorrectDialog
        open={correctOpen}
        stages={correctionStages}
        submitting={correctMutation.isLoading}
        onClose={() => setCorrectOpen(false)}
        onConfirm={(to, reason) => correctMutation.mutate({ to, reason })}
      />

      <RmCancelDialog
        open={cancelOpen}
        submitting={cancelMutation.isLoading}
        onClose={() => setCancelOpen(false)}
        onConfirm={(reason) => cancelMutation.mutate(reason)}
      />
    </div>
  );
}

/**
 * The hero, like the book-order Print Shop ticket: state first, the one next
 * action as a real button, the rare repairs (correct, cancel) as small links,
 * and the stepper In setup → Sent to print → Printed → Shipped.
 */
function RmHero({
  job,
  action,
  busy,
  onReady,
  onAdvance,
  onCorrect,
  onCancel,
}: {
  job: RmJobDetail;
  action: ReturnType<typeof heroAction>;
  busy: boolean;
  onReady: () => void;
  onAdvance: () => void;
  onCorrect?: () => void;
  onCancel?: () => void;
}) {
  const issue = issueLabel(job.issue.month, job.issue.year);
  const actionButton =
    'inline-flex h-10 items-center rounded-lg bg-white px-4 text-sm font-bold text-primary-700 shadow-sm transition hover:bg-primary-50 disabled:opacity-60';
  const quietPill =
    'inline-flex h-10 items-center rounded-lg border border-white/50 bg-white/10 px-4 text-sm font-semibold text-white/90';

  return (
    <section
      data-testid="rm-hero"
      className="mb-6 rounded-2xl bg-gradient-to-br from-primary-700 to-primary-500 px-6 py-6 text-white shadow-md"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-white/80">
            {formatMessage(detail.eyebrow, { issue })}
          </p>
          <h1 className="mt-1 break-words text-xl font-extrabold lg:text-2xl">
            {job.member.name}
          </h1>
          <p className="mt-0.5 text-sm text-white/90">{job.member.email}</p>
        </div>

        <div className="flex flex-col items-end gap-2.5">
          <span
            data-testid="rm-stage-badge"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
              job.stage === 'shipped'
                ? 'bg-success-50 text-success-700'
                : job.stage === 'cancelled'
                  ? 'bg-error-50 text-error-700'
                  : 'bg-white text-primary-700'
            }`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {stageLabel(job.stage)}
          </span>

          {action.kind === 'ready' && (
            <button
              type="button"
              data-testid="rm-ready-button"
              disabled={busy}
              onClick={onReady}
              className={actionButton}
            >
              {detail.hero.markReady}
            </button>
          )}
          {action.kind === 'readyBlocked' && (
            // Where the action would be: the job has nothing to print yet.
            <span data-testid="rm-ready-blocked" className={quietPill}>
              {detail.hero.markReadyBlocked}
            </span>
          )}
          {action.kind === 'advance' && (
            <button
              type="button"
              data-testid="rm-advance-button"
              disabled={busy}
              onClick={onAdvance}
              className={actionButton}
            >
              {formatMessage(detail.hero.advance, {
                to: stageLabel(action.to).toLowerCase(),
              })}
            </button>
          )}
          {action.kind === 'terminal' && (
            <span data-testid="rm-advance-none" className={quietPill}>
              {action.text}
            </span>
          )}

          {(onCorrect || onCancel) && (
            <div className="flex items-center gap-3">
              {onCorrect && (
                <button
                  type="button"
                  data-testid="rm-correct-button"
                  onClick={onCorrect}
                  className={HERO_LINK}
                >
                  {detail.hero.correctStage}
                </button>
              )}
              {onCancel && (
                <button
                  type="button"
                  data-testid="rm-cancel-button"
                  onClick={onCancel}
                  className={HERO_LINK}
                >
                  {detail.hero.cancelJob}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {job.cancellation && (
        <p
          data-testid="rm-cancellation"
          className="mt-4 whitespace-pre-wrap break-words rounded-lg border border-white/40 bg-white/10 px-4 py-3 text-sm text-white"
        >
          {formatMessage(detail.hero.cancelledLine, {
            reason: job.cancellation.reason,
            by: job.cancellation.cancelledBy,
            date: formatDateTime(job.cancellation.cancelledAt),
          })}
        </p>
      )}

      <ol className="mt-5 flex items-center overflow-x-auto" data-testid="rm-stepper">
        {stepStates(job).map(({ step, state }, index) => {
          const reached = state !== 'todo';
          return (
            <React.Fragment key={step}>
              {index > 0 && (
                <span
                  className={`mx-2 h-0.5 min-w-[16px] flex-1 ${
                    reached ? 'bg-white' : 'bg-white/30'
                  }`}
                />
              )}
              <li
                className="flex shrink-0 items-center gap-2 text-[11px] font-semibold"
                data-testid={`rm-step-${step}`}
                data-state={state}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${
                    reached
                      ? 'bg-white text-primary-700'
                      : 'bg-white/25 text-white/70'
                  }`}
                >
                  {state === 'done'
                    ? '✓'
                    : state === 'current'
                      ? '●'
                      : state === 'cancelled'
                        ? '✕'
                        : ''}
                </span>
                <span
                  className={
                    state === 'current' || state === 'cancelled'
                      ? 'font-bold'
                      : 'text-white/90'
                  }
                >
                  {state === 'cancelled'
                    ? `${stageLabel(step)} · ${detail.stepper.cancelled}`
                    : stageLabel(step)}
                </span>
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </section>
  );
}

/**
 * The magazine PDF and the covers, with the member's note attached to them.
 * Each file is its own action; upload / replace shows only while DS allows it.
 */
function RmPrintFiles({
  job,
  actorEmail,
  onUploaded,
  onStale,
}: {
  job: RmJobDetail;
  actorEmail: string;
  onUploaded: () => void;
  onStale: () => void;
}) {
  const magazine: RmFileStatus = job.files.find(
    (file) => file.kind === 'magazine',
  ) ?? { kind: 'magazine', available: false, reason: 'not-generated' };
  const covers = job.files.find((file) => file.kind === 'covers');
  // An imported or batch job has no DS magazine, so there are no covers to
  // make: saying "never generated" there would read as a failure. A stored
  // reference (any reason but "not-generated") always shows: an imported job's
  // covers is the HubSpot copy, and hiding it on a failed check would leave the
  // operator a job with no file at all.
  const coversStored = Boolean(
    covers && (covers.available || covers.reason !== 'not-generated'),
  );
  const showCovers = Boolean(covers && (coversStored || job.magazineId));

  return (
    <section className={CARD} data-testid="rm-files">
      <h2 className={CARD_TITLE}>{detail.files.heading}</h2>
      {job.specialInstructions && (
        <p
          data-testid="rm-files-note"
          className="mt-3 whitespace-pre-wrap break-words rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <span className="font-semibold">{detail.files.memberNote}</span>{' '}
          {job.specialInstructions}
        </p>
      )}
      <div className="mt-4 flex flex-col gap-3">
        <RmFileRow
          job={job}
          file={magazine}
          canUpload={Boolean(job.actions?.uploadMagazine)}
          actorEmail={actorEmail}
          onUploaded={onUploaded}
          onStale={onStale}
        />
        {showCovers && covers && (
          <RmFileRow
            job={job}
            file={covers}
            canUpload={false}
            actorEmail={actorEmail}
            onUploaded={onUploaded}
            onStale={onStale}
          />
        )}
      </div>
    </section>
  );
}

function RmFileRow({
  job,
  file,
  canUpload,
  actorEmail,
  onUploaded,
  onStale,
}: {
  job: RmJobDetail;
  file: RmFileStatus;
  canUpload: boolean;
  actorEmail: string;
  onUploaded: () => void;
  onStale: () => void;
}) {
  const [opening, setOpening] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const kind: RmFileKind = file.kind;
  const title = kind === 'magazine' ? detail.files.magazine : detail.files.covers;
  const uploaded = Boolean(file.uploadedBy || file.name);

  // A fresh signed URL per click — never stored: it dies in five minutes.
  //
  // The tab is opened SYNCHRONOUSLY, inside the click, then pointed at the URL
  // once it resolves. Calling window.open AFTER the await runs outside the user
  // gesture and is silently eaten by the popup blocker — the operator would
  // click and see nothing. No 'noopener' feature: with it window.open returns
  // null and the tab can never be pointed; the opener is cut by hand instead.
  // If the tab could not be opened, or the fetch fails, the ABO tab stays put.
  const preview = async () => {
    if (opening) return;
    const tab = window.open('', '_blank');
    if (!tab) {
      toast.error(detail.toast.previewBlocked);
      return;
    }
    // The signed URL's page must not reach back into ABO.
    tab.opener = null;
    setOpening(true);
    try {
      const { url } = await fetchRmFileDownload(job.jobId, kind, 'inline');
      tab.location.href = url;
    } catch {
      tab.close();
      toast.error(detail.toast.downloadFailed);
    } finally {
      setOpening(false);
    }
  };

  // The URL is signed as an attachment, so navigating to it saves the file
  // under its readable name and the ABO page stays. An <a download> would not:
  // browsers ignore that attribute on a cross-origin URL.
  // Known limit: S3 applies the attachment disposition only to a 200. If the
  // object vanished after DS signed it, its XML error page replaces ABO here
  // (Back returns) — the same as the old <a download> did.
  const download = async () => {
    if (opening) return;
    setOpening(true);
    try {
      const { url } = await fetchRmFileDownload(job.jobId, kind, 'attachment');
      window.location.assign(url);
    } catch {
      toast.error(detail.toast.downloadFailed);
    } finally {
      setOpening(false);
    }
  };

  const onPicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    // Cleared so picking the same file again still fires a change.
    event.target.value = '';
    if (!picked) return;
    const clientError = magazineClientError(picked);
    if (clientError) {
      toast.error(clientError);
      return;
    }
    setUploadPercent(0);
    try {
      await uploadRmMagazine(job.jobId, picked, actorEmail, setUploadPercent);
      toast.success(detail.files.uploaded);
      onUploaded();
    } catch (error) {
      toast.error(errorMessage(error, detail.files.uploadFailed));
      if (refusalCode(error) === 'RM_PRINT_JOB_FILE_LOCKED') {
        onStale();
      }
    } finally {
      setUploadPercent(null);
    }
  };

  let subtitle: string;
  if (file.available) {
    subtitle = file.name ?? detail.files.generated;
  } else if (
    kind === 'magazine' &&
    (!job.magazineId || job.source === 'hubspot_import') &&
    (!file.reason || file.reason === 'not-generated')
  ) {
    // Only "nothing was ever made" is "no PDF yet". A stored reference whose
    // check failed or whose object is missing keeps its own wording: "could
    // not check" is never folded into "not there". An imported job carries
    // only the HubSpot covers (the interior is the same for every member), so
    // a linked DS magazine there does not make "never generated" true.
    subtitle = detail.files.noMagazine;
  } else {
    subtitle = fileUnavailableWording(file.reason);
  }

  return (
    <div
      data-testid={`rm-file-${kind}`}
      className="flex flex-wrap items-center gap-4 rounded-lg border border-neutral-200 px-4 py-3"
    >
      <div className="flex h-14 w-11 flex-none items-center justify-center rounded-md border border-primary-100 bg-primary-50 text-[11px] font-extrabold tracking-wide text-primary-700">
        {detail.files.badge}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-neutral-800">{title}</p>
        <p
          className={`break-all text-sm ${
            file.available ? 'text-neutral-700' : 'italic text-neutral-500'
          }`}
          data-testid={
            file.available ? `rm-file-name-${kind}` : `rm-file-unavailable-${kind}`
          }
        >
          {subtitle}
        </p>
        {file.available && uploaded && (
          <p data-testid={`rm-file-uploaded-${kind}`} className="text-xs text-neutral-500">
            {formatMessage(detail.files.uploadedBy, {
              size: formatFileSize(file.size),
              by: file.uploadedBy ?? '—',
              date: formatDateTime(file.uploadedAt),
            })}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {file.available && (
          <>
            <button
              type="button"
              data-testid={`rm-file-preview-${kind}`}
              disabled={opening}
              onClick={preview}
              className={SMALL_BUTTON}
            >
              <HiOutlineExternalLink className="h-4 w-4" />
              {detail.files.preview}
            </button>
            <button
              type="button"
              data-testid={`rm-file-download-${kind}`}
              disabled={opening}
              onClick={download}
              className={SMALL_BUTTON}
            >
              <HiOutlineDownload className="h-4 w-4" />
              {detail.files.download}
            </button>
          </>
        )}
        {canUpload && (
          <>
            <input
              ref={input}
              type="file"
              accept="application/pdf"
              hidden
              data-testid={`rm-file-input-${kind}`}
              onChange={onPicked}
            />
            <button
              type="button"
              data-testid={`rm-file-upload-${kind}`}
              disabled={uploadPercent !== null}
              onClick={() => input.current?.click()}
              className={file.available ? LINK_BUTTON : SMALL_BUTTON}
            >
              {uploadPercent !== null
                ? formatMessage(detail.files.uploading, { percent: uploadPercent })
                : file.available
                  ? detail.files.replace
                  : detail.files.upload}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** Labels next to values; origin facts (HubSpot ids) small, last. */
function RmAbout({ job }: { job: RmJobDetail }) {
  const customization = customizationDisplay(job);
  const serviceId = job.externalRefs?.hubspotServiceId ?? job.hubspotServiceId;
  const ticketId = job.externalRefs?.hubspotTicketId ?? job.hubspotTicketId;
  const inWork =
    job.stage === 'in_setup' ||
    job.stage === 'sent_to_print' ||
    job.stage === 'printed';
  const origin = [
    serviceId && formatMessage(detail.facts.hubspotService, { id: serviceId }),
    ticketId && formatMessage(detail.facts.hubspotTicket, { id: ticketId }),
  ].filter(Boolean) as string[];
  // The import run id means something to support, not to the operator: a
  // tooltip on the origin line rather than text in it.
  const originTitle = job.importRunId
    ? formatMessage(detail.facts.importRun, { id: job.importRunId })
    : undefined;

  const link = (url: string | undefined, testId: string, absent: string) =>
    url ? (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className="text-primary-600 hover:underline"
      >
        {detail.facts.open}
      </a>
    ) : (
      <span className="italic text-neutral-400">{absent}</span>
    );

  const rows: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: detail.facts.source,
      value: (
        <span
          data-testid="rm-facts-source"
          className={`rounded-md px-2 py-0.5 text-xs font-medium ${sourceChipClass(
            job.source,
          )}`}
        >
          {sourceLabel(job.source)}
        </span>
      ),
    },
    { label: detail.facts.format, value: formatLabel(job.format) },
    {
      label: detail.facts.customization,
      value: (
        <span data-testid="rm-facts-customization" title={customization.hint}>
          {customization.text}
        </span>
      ),
    },
  ];
  if (inWork) {
    // An in-setup job's age is time in setup (it was never sent), the same
    // wording as the lane's column.
    rows.push({
      label:
        job.stage === 'in_setup' ? detail.facts.inSetupFor : detail.facts.waiting,
      value: ageText(job.ageDays),
    });
  }
  rows.push(
    {
      label: detail.facts.memberSite,
      // Absent member-site link is a STATED condition, never a blank cell.
      value: link(job.links?.memberSite, 'rm-link-member-site', detail.facts.memberSiteAbsent),
    },
    {
      label: detail.facts.proof,
      value: link(job.links?.proof, 'rm-link-proof', detail.facts.absent),
    },
  );
  if (job.stage === 'shipped' || job.links?.shippedMagazine) {
    rows.push({
      label: detail.facts.shippedMagazine,
      value: link(job.links?.shippedMagazine, 'rm-link-shipped', detail.facts.absent),
    });
  }

  return (
    <section className={CARD} data-testid="rm-facts">
      <h2 className={CARD_TITLE}>{detail.facts.heading}</h2>
      <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.label}>
            <dt className="text-xs text-neutral-400">{row.label}</dt>
            <dd className="text-sm text-neutral-800">{row.value}</dd>
          </div>
        ))}
      </dl>
      {origin.length > 0 && (
        <p
          data-testid="rm-facts-origin"
          title={originTitle}
          className="mt-4 border-t border-neutral-100 pt-3 text-xs text-neutral-400"
        >
          {origin.join(' · ')}
        </p>
      )}
    </section>
  );
}

/** Oldest first, each with a who chip, what happened and its detail. */
function RmHistory({ job }: { job: RmJobDetail }) {
  const entries = historyChronological(job.history);
  return (
    <section className={CARD} data-testid="rm-history">
      <h2 className={CARD_TITLE}>{detail.history.heading}</h2>
      {entries.length === 0 ? (
        <p className="mt-3 text-sm text-neutral-400">{detail.history.empty}</p>
      ) : (
        <ol className="mt-3 space-y-4">
          {entries.map((entry, index) => {
            const extra = historyDetail(entry);
            return (
              <li
                key={`${entry.to}-${entry.at ?? ''}-${index}`}
                data-testid={`rm-history-entry-${index}`}
                className="border-l-2 border-neutral-100 pl-3"
              >
                <p className="text-xs text-neutral-400">{formatDateTime(entry.at)}</p>
                <div className="mt-0.5 flex flex-wrap items-center gap-2">
                  <span
                    data-testid={`rm-history-actor-${index}`}
                    className={`rounded-md px-2 py-0.5 text-xs font-medium ${actorChipClass(
                      entry.actor,
                    )}`}
                  >
                    {actorLabel(entry.actor)}
                  </span>
                  <span className="text-sm text-neutral-700">
                    {historySummary(entry)}
                  </span>
                  {entry.correction && (
                    <span
                      data-testid={`rm-history-correction-${index}`}
                      className="rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
                    >
                      {detail.history.correctionBadge}
                    </span>
                  )}
                </div>
                {extra && (
                  <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-neutral-500">
                    {extra}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/** Backwards-only repair: pick the earlier stage, say why. */
function RmCorrectDialog({
  open,
  stages,
  submitting,
  onClose,
  onConfirm,
}: {
  open: boolean;
  stages: RmCorrectionStage[];
  submitting: boolean;
  onClose: () => void;
  onConfirm: (to: RmCorrectionStage, reason: string) => void;
}) {
  // The nearest earlier stage is the usual mistake, so it is the default.
  const [to, setTo] = useState<RmCorrectionStage | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const target = to ?? stages[stages.length - 1] ?? null;

  // Each opening starts clean, however the last one ended.
  useEffect(() => {
    if (open) {
      setTo(null);
      setReason('');
      setReasonError(false);
    }
  }, [open]);

  const close = () => {
    if (submitting) return;
    onClose();
  };

  const confirm = () => {
    if (!target) return;
    if (reason.trim() === '') {
      setReasonError(true);
      return;
    }
    onConfirm(target, reason.trim());
  };

  return (
    <DefaultModal
      title={detail.correct.title}
      showModal={open}
      setShowModal={(show) => {
        if (!show) close();
      }}
      size="md"
    >
      {open && (
        <div className="space-y-4" data-testid="rm-correct-dialog">
          <p className="text-sm text-neutral-600">{detail.correct.hint}</p>
          <fieldset className="flex flex-col gap-1">
            <legend className="mb-1 text-sm font-semibold text-neutral-800">
              {detail.correct.stageLabel}
            </legend>
            {stages.map((stage) => (
              <label
                key={stage}
                className="flex min-h-[36px] items-center gap-2 text-sm text-neutral-800"
              >
                <input
                  type="radio"
                  name="rm-correct-stage"
                  data-testid={`rm-correct-${stage}`}
                  checked={target === stage}
                  onChange={() => setTo(stage)}
                  className="h-4 w-4 accent-primary-500"
                />
                {stageLabel(stage)}
              </label>
            ))}
          </fieldset>
          <label className="block text-sm font-semibold text-neutral-800">
            {detail.correct.reasonLabel}
            <textarea
              data-testid="rm-correction-reason"
              className="mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm font-normal"
              rows={3}
              maxLength={CORRECTION_REASON_MAX_LENGTH}
              placeholder={detail.correct.reasonPlaceholder}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (e.target.value.trim() !== '') setReasonError(false);
              }}
            />
          </label>
          {reasonError && (
            <p data-testid="rm-correction-reason-error" className="text-sm text-red-600">
              {detail.correct.reasonRequired}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="OUTLINED" disabled={submitting} onClick={close}>
              {detail.correct.keep}
            </Button>
            <Button
              variant="DANGER"
              data-testid="rm-correct-confirm"
              isProcessing={submitting}
              disabled={submitting || !target}
              onClick={confirm}
            >
              {detail.correct.confirm}
            </Button>
          </div>
        </div>
      )}
    </DefaultModal>
  );
}

/** The minimal cancel: one reason field, like the book-order cancel link. */
function RmCancelDialog({
  open,
  submitting,
  onClose,
  onConfirm,
}: {
  open: boolean;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const reasonError = validateCancelReason(reason);

  // Each opening starts clean, however the last one ended.
  useEffect(() => {
    if (open) {
      setReason('');
      setTouched(false);
    }
  }, [open]);

  const close = () => {
    if (submitting) return;
    onClose();
  };

  return (
    <DefaultModal
      title={detail.cancel.title}
      showModal={open}
      setShowModal={(show) => {
        if (!show) close();
      }}
      size="md"
    >
      {open && (
        <div className="flex flex-col gap-4" data-testid="rm-cancel-dialog">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="rm-cancel-reason"
              className="text-sm font-semibold text-neutral-800"
            >
              {detail.cancel.reasonLabel}
            </label>
            <input
              id="rm-cancel-reason"
              data-testid="rm-cancel-reason"
              type="text"
              value={reason}
              placeholder={detail.cancel.reasonPlaceholder}
              aria-invalid={touched && reasonError !== null}
              onChange={(event) => setReason(event.target.value)}
              onBlur={() => setTouched(true)}
              className="h-10 rounded-md border border-neutral-300 px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            {touched && reasonError && (
              <span data-testid="rm-cancel-reason-error" className="text-xs text-red-600">
                {formatMessage(
                  reasonError === 'too_short'
                    ? detail.cancel.reasonTooShort
                    : detail.cancel.reasonTooLong,
                  { min: CANCEL_REASON_MIN_LENGTH, max: CANCEL_REASON_MAX_LENGTH },
                )}
              </span>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="OUTLINED" disabled={submitting} onClick={close}>
              {detail.cancel.keep}
            </Button>
            <Button
              variant="DANGER"
              data-testid="rm-cancel-confirm"
              isProcessing={submitting}
              disabled={submitting || reasonError !== null}
              onClick={() => onConfirm(reason.trim())}
            >
              {detail.cancel.confirm}
            </Button>
          </div>
        </div>
      )}
    </DefaultModal>
  );
}
